import React from 'react';
import { ThermometerSnowflake, Thermometer, GaugeCircle } from 'lucide-react';
import { StarterStorage } from '../../types/baking';
import { getTemperatureWarning } from '../../lib/utils';

interface StorageSelectorProps {
  value: StarterStorage;
  onChange: (storage: StarterStorage) => void;
  disableFridge?: boolean;
  roomTemperature?: number;
  onRoomTemperatureChange?: (temp: number) => void;
}

export function StorageSelector({ 
  value, 
  onChange, 
  disableFridge = false,
  roomTemperature,
  onRoomTemperatureChange 
}: StorageSelectorProps) {
  const storageOptions = [
    {
      id: 'room',
      label: 'Room Temperature',
      description: roomTemperature ? `${roomTemperature}°C` : '20-25°C',
      icon: value === 'room' ? GaugeCircle : Thermometer,
      color: 'text-amber-300'
    },
    {
      id: 'fridge',
      label: 'Refrigerated',
      description: '4-8°C',
      icon: ThermometerSnowflake,
      color: 'text-blue-300'
    }
  ];

  const warning = roomTemperature ? getTemperatureWarning(roomTemperature) : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {storageOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.id;
          const isDisabled = option.id === 'fridge' && disableFridge;
          
          return (
            <label
              key={option.id}
              className={`relative flex flex-col items-center p-4 border rounded-xl transition-all duration-200 ${
                isDisabled
                  ? 'opacity-50 cursor-not-allowed border-white/10 bg-white/5'
                  : isSelected
                  ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10 cursor-pointer'
                  : 'border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer'
              }`}
            >
              <input
                type="radio"
                name="storage"
                value={option.id}
                checked={isSelected}
                onChange={() => !isDisabled && onChange(option.id as StarterStorage)}
                className="sr-only"
                disabled={isDisabled}
              />
              
              <Icon className={`w-8 h-8 mb-2 ${option.color}`} />
              
              <div className="text-center">
                <span className="block font-medium text-sm text-white">
                  {option.label}
                </span>
                <span className="block text-xs text-white/70 mt-1">
                  {option.description}
                </span>
              </div>

              {isSelected && !isDisabled && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-black/20" />
              )}
            </label>
          );
        })}
      </div>

      {value === 'room' && onRoomTemperatureChange && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-white/90">
            <span>Room Temperature</span>
            <span>{roomTemperature || 20}°C</span>
          </div>
          <input
            type="range"
            min="10"
            max="35"
            step="0.5"
            value={roomTemperature || 20}
            onChange={(e) => onRoomTemperatureChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <div className="flex justify-between text-xs text-white/50">
            <span>10°C</span>
            <span>35°C</span>
          </div>
        </div>
      )}

      {warning && (
        <div className="p-3 bg-amber-500/10 backdrop-blur-sm rounded-lg border border-amber-500/20">
          <p className="text-sm text-amber-200">{warning}</p>
        </div>
      )}
    </div>
  );
}